import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Card,
  Typography,
  Skeleton,
  Tooltip,
} from "antd";

import {
  DollarOutlined,
  TeamOutlined,
  CalendarOutlined,
  ReloadOutlined,
  WalletOutlined,
} from "@ant-design/icons";

import { motion } from "framer-motion";

import api from "../../api/api";
import { useAcademicYear } from "../../AcademicYearContext";

const { Text } = Typography;

const YearlySchoolFeeCount = ({ theme }) => {
  const { selectedYear } = useAcademicYear();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const [fee, setFee] = useState({
    academic_year: "",
    total_students: 0,
    total_fee: 0,
  });

  const fetchYearlyFee = useCallback(async () => {
    if (!selectedYear?.id) return;

    try {
      setLoading(true);
      setError(false);

      const res = await api.get(
        `/yearly-school-fees/?academic_year=${selectedYear.id}`
      );

      setFee({
        academic_year: res.data?.academic_year || "",
        total_students: Number(
          res.data?.total_students || 0
        ),
        total_fee: Number(
          res.data?.total_fee || 0
        ),
      });
    } catch (error) {
      console.error(
        "Failed to fetch yearly school fees:",
        error
      );
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [selectedYear?.id]);

  useEffect(() => {
    fetchYearlyFee();
  }, [fetchYearlyFee]);

  useEffect(() => {
    const handleUpdate = () => {
      fetchYearlyFee();
    };

    window.addEventListener(
      "academic-year-updated",
      handleUpdate
    );

    window.addEventListener(
      "academicYearUpdated",
      handleUpdate
    );

    window.addEventListener(
      "feeCountUpdated",
      handleUpdate
    );

    return () => {
      window.removeEventListener(
        "academic-year-updated",
        handleUpdate
      );

      window.removeEventListener(
        "academicYearUpdated",
        handleUpdate
      );

      window.removeEventListener(
        "feeCountUpdated",
        handleUpdate
      );
    };
  }, [fetchYearlyFee]);

  const formattedFee = useMemo(() => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(fee.total_fee || 0);
  }, [fee.total_fee]);

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ height: "100%" }}
      >
        <Card
          bordered={false}
          style={{
            height: "100%",
            minHeight: 218,
            borderRadius: 22,
            background: theme.card,
            border: `1px solid ${theme.border}`,
            boxShadow:
              "0 14px 40px rgba(6,42,75,.07)",
          }}
          styles={{
            body: {
              padding: 22,
            },
          }}
        >
          <Skeleton
            active
            paragraph={{ rows: 4 }}
          />
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{
        y: -5,
        transition: { duration: 0.2 },
      }}
      style={{ height: "100%" }}
    >
      <Card
        bordered={false}
        style={{
          height: "100%",
          minHeight: 218,
          borderRadius: 22,
          overflow: "hidden",
          position: "relative",
          background: theme.card,
          border: `1px solid ${theme.border}`,
          boxShadow:
            "0 14px 40px rgba(6,42,75,.07)",
        }}
        styles={{
          body: {
            padding: 0,
            height: "100%",
          },
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: 4,
            background:
              "linear-gradient(90deg,#C0750D,#F6C944,#5F6622)",
          }}
        />

        <div
          style={{
            padding: 22,
            height: "100%",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
              }}
            >
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 13,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background:
                    "linear-gradient(135deg,#C0750D,#F6C944)",
                  color: "#fff",
                  fontSize: 20,
                  boxShadow:
                    "0 7px 18px rgba(192,117,13,.22)",
                }}
              >
                <DollarOutlined />
              </div>

              <div>
                <Text
                  style={{
                    display: "block",
                    color: theme.text,
                    fontWeight: 700,
                    fontSize: 14,
                  }}
                >
                  Fee Collection
                </Text>

                <Text
                  style={{
                    color: theme.secondary,
                    fontSize: 11,
                  }}
                >
                  {fee.academic_year ||
                    selectedYear?.name ||
                    "Academic Year"}
                </Text>
              </div>
            </div>

            <Tooltip title="Refresh">
              <button
                type="button"
                onClick={fetchYearlyFee}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 10,
                  border: `1px solid ${theme.border}`,
                  background: theme.soft,
                  color: theme.secondary,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ReloadOutlined />
              </button>
            </Tooltip>
          </div>

          <div
            style={{
              marginTop: 18,
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <WalletOutlined
              style={{
                color: "#C0750D",
                fontSize: 17,
              }}
            />

            <span
              style={{
                fontSize: 31,
                lineHeight: 1,
                fontWeight: 800,
                letterSpacing: "-1px",
                color: theme.text,
              }}
            >
              {error ? "—" : formattedFee}
            </span>
          </div>

          <Text
            style={{
              display: "block",
              marginTop: 7,
              color: theme.secondary,
              fontSize: 12,
            }}
          >
            Total school fees recorded for this year
          </Text>

          {error ? (
            <button
              type="button"
              onClick={fetchYearlyFee}
              style={{
                marginTop: 16,
                border: 0,
                background: "transparent",
                padding: 0,
                color: "#C0750D",
                cursor: "pointer",
                fontWeight: 700,
              }}
            >
              Unable to load. Try again
            </button>
          ) : (
            <div
              style={{
                marginTop: 18,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
              }}
            >
              <div
                style={{
                  padding: "11px 12px",
                  borderRadius: 14,
                  background:
                    theme.mode === "dark"
                      ? "rgba(6,42,75,.18)"
                      : "#F4F8FA",
                  border: `1px solid ${theme.border}`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                  }}
                >
                  <TeamOutlined
                    style={{
                      color: "#062A4B",
                    }}
                  />

                  <Text
                    style={{
                      color: theme.secondary,
                      fontSize: 11,
                    }}
                  >
                    Students
                  </Text>
                </div>

                <strong
                  style={{
                    display: "block",
                    marginTop: 4,
                    color: theme.text,
                    fontSize: 18,
                  }}
                >
                  {fee.total_students.toLocaleString()}
                </strong>
              </div>

              <div
                style={{
                  padding: "11px 12px",
                  borderRadius: 14,
                  background:
                    theme.mode === "dark"
                      ? "rgba(192,117,13,.12)"
                      : "#FCF8F1",
                  border: `1px solid ${
                    theme.mode === "dark"
                      ? "rgba(192,117,13,.25)"
                      : "#F0E4CF"
                  }`,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                  }}
                >
                  <CalendarOutlined
                    style={{
                      color: "#C0750D",
                    }}
                  />

                  <Text
                    style={{
                      color: theme.secondary,
                      fontSize: 11,
                    }}
                  >
                    Year / Term
                  </Text>
                </div>

                <strong
                  style={{
                    display: "block",
                    marginTop: 4,
                    color: theme.text,
                    fontSize: 13,
                  }}
                >
                  {fee.academic_year ||
                    selectedYear?.name ||
                    "—"}
                </strong>
              </div>
            </div>
          )}
        </div>
      </Card>
    </motion.div>
  );
};

export default YearlySchoolFeeCount;